import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'

import { IconButton, Table, TableBody, TableHead, TableRow } from '@mui/material'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'

import StatisticRow from './rows/StatisticRow'
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'

import {
  StatisticsEditTableAddNewButtonCaption,
  StatisticsEditTableTableCell,
} from '../../styles/components/StatisticsEditTableStyle'
import invariant from 'tiny-invariant'
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge'
import { reorderWithEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/reorder-with-edge'
import statisticsApi from '../../api/statistics'
import useHandleError from '../../api/useHandleError'
import { useParams } from 'react-router-dom'
import { setViewportWidth } from '../../useViewPort'

const StatisticsEditTable = ({ rowsData }) => {
  const tbodyRef = useRef(null)
  const { id } = useParams()
  const [tableRows, setTableRows] = useState(rowsData)
  const [isDragDisabled, setIsDragDisabled] = useState(false)
  const handleError = useHandleError()
  setTimeout(() => setViewportWidth())
  /* Add new empty row */
  const handleAdd = () => {
    setTableRows([
      ...tableRows,
      {
        id: `tmp-id-${Date.now()}`,
        name: '',
        count: null,
        filters: {},
        isNew: true,
        order: tableRows.length,
      },
    ])
  }

  useEffect(() => {
    setTableRows(rowsData)
  }, [rowsData])

  useEffect(() => {
    // Filter temp lines and empty
    if (tableRows && !tableRows.some((item) => typeof item.id == 'string' && item.id.startsWith('tmp-id'))) {
      // Reorder to be sure correct order
      saveNewOrder(assignCorrectOrder(tableRows))
    }
    setTimeout(() => setViewportWidth())
  }, [tableRows])

  const renderTableBody =
    tableRows &&
    tableRows.length !== 0 &&
    tableRows.map((row, index) => {
      return (
        <StatisticRow
          key={`${index}-${row.id}`}
          index={index}
          row={row}
          tableRows={tableRows}
          setTableRows={setTableRows}
          isDragDisabled={isDragDisabled}
          setIsDragDisabled={setIsDragDisabled}
        />
      )
    })

  useEffect(() => {
    const cleanup = dropTargetForElements({
      element: tbodyRef.current,
      onDrop: ({ source, location }) => {
        if (location.current.dropTargets.length === 0) {
          return
        }

        const target = location.current.dropTargets[0]
        if (source.data.id === target.data.id) {
          return
        }

        if (source.data.type === 'table-row') {
          const closestEdgeOfTarget = extractClosestEdge(target.data)
          invariant(closestEdgeOfTarget === 'top' || closestEdgeOfTarget === 'bottom')

          setTableRows((rows) => {
            const startIndex = rows.findIndex((row) => row.id === source.data.id)
            const indexOfTarget = rows.findIndex((row) => row.id === target.data.id)
            return reorderWithEdge({
              list: rows,
              closestEdgeOfTarget,
              startIndex,
              indexOfTarget,
              axis: 'vertical',
            }).map((row, idx) => {
              row.order = idx
              return row
            })
          })
        }
      },
    })
    return () => cleanup()
  }, [rowsData])

  const assignCorrectOrder = (rowsList) => {
    return rowsList.map((row, index) => {
      row.order = index
      return row
    })
  }

  const saveNewOrder = (rowsList) => {
    statisticsApi.reorderRows(rowsList, id).catch(handleError)
  }

  return (
    <Table aria-label="collapsible table">
      <StatisticsEditTableAddNewButtonCaption>
        <IconButton onClick={handleAdd} aria-label="addFilterButton">
          <AddCircleOutlineIcon />
        </IconButton>
      </StatisticsEditTableAddNewButtonCaption>

      <TableHead>
        <TableRow>
          <StatisticsEditTableTableCell align="center">Name</StatisticsEditTableTableCell>
          <StatisticsEditTableTableCell align="center">Filtered value</StatisticsEditTableTableCell>
          <StatisticsEditTableTableCell align="center">Actions</StatisticsEditTableTableCell>
        </TableRow>
      </TableHead>
      <TableBody aria-label="filtersTable" ref={tbodyRef}>
        {renderTableBody}
      </TableBody>
    </Table>
  )
}

StatisticsEditTable.propTypes = {
  rowsData: PropTypes.array,
}

export default StatisticsEditTable
